import React, { Component } from "react";
import clsx from "clsx";

import BoModal from "../common/BoModal";
import BackofficeService from "../../../services/BackofficeService";
import { I } from "../common/icons";
import { SERVICES, SERVICE_ORDER, STATUSES, STATUS_ORDER, FREQUENCIES, REPEAT_RULES, ACCOUNT_TYPES, CADENCES, FIRST_SENDS, GROUP_PRESETS, ROLE_ORDER, ROLES } from "../backofficeConstants";
import { today, addDays, hrs } from "../backofficeUtils";

const NEW_GROUP = "__new__";

function defaultAccountsFor(title, chart) {
	if (/^reconcile bank/i.test(title)) return chart.filter(a => a.type === "bank" || a.type === "cc").filter(a => !/trust/i.test(a.name));
	if (/^reconcile clearing/i.test(title)) return chart.filter(a => a.type === "clearing");
	if (/trust account reconciliation/i.test(title)) return chart.filter(a => /trust/i.test(a.name));
	return [];
}

/**
 * Create / edit a backoffice task: schedule, people, time budget, tracked accounts, grouping and
 * the optional client email automation.
 */
class TaskModal extends Component {
	constructor(props) {
		super(props);
		const { task, initialClientId, initialService, initialDate, clients } = props;
		const clientId = task ? task.clientId : initialClientId || (clients[0] ? clients[0].clientId : null);
		const start = task ? task.startDate : initialDate || today();
		const e = task && task.emailAutomation;
		const groupName = task ? task.groupName || "" : "";
		this.state = {
			saving: false,
			error: null,
			title: task ? task.title : "",
			clientId,
			service: task ? task.service : initialService || "monthend",
			description: task ? task.description || task.hint || "" : "",
			startDate: start,
			dueDate: task ? task.dueDate : addDays(start, 2),
			frequency: task ? task.frequency : initialService === "ap" ? "weekly" : initialService === "payroll" ? "biweekly" : "monthly",
			repeatRule: task ? task.repeatRule : "same_day",
			preparerUserId: task ? task.preparerUserId || "" : "",
			reviewerUserId: task ? task.reviewerUserId || "" : "",
			managerUserId: task ? task.managerUserId || "" : "",
			budget: task ? Object.assign({}, task.budget) : { preparer: 1, reviewer: 0.25, manager: 0 },
			logged: task ? Object.assign({}, task.logged) : { preparer: 0, reviewer: 0, manager: 0 },
			status: task ? task.status : "not_started",
			groupSelect: groupName ? (GROUP_PRESETS.includes(groupName) ? groupName : NEW_GROUP) : "",
			groupName,
			accountsOn: !!(task && task.accounts.length),
			accounts: task ? task.accounts.map(a => ({ chartOfAccountId: a.chartOfAccountId, glCode: a.glCode, name: a.name, type: a.type, isDone: a.isDone })) : [],
			chart: [],
			newAccount: "",
			newAccountType: "bank",
			emailOn: !!e,
			email: {
				subject: e ? e.subject : "",
				body: e ? e.body || "" : "Hi {{contact_first_name}}, please upload your {{period}} bank and credit card statements via the portal link below.",
				firstSend: e ? e.firstSend : "on_start",
				sendTime: e ? e.sendTime : "09:00",
				cadence: e ? e.cadence : "every_3_days",
				markOnCalendar: e ? e.markOnCalendar : true,
				recipientEmail: e ? e.recipientEmail : "",
				recipientName: e ? e.recipientName || "" : "",
			},
		};
	}

	componentDidMount() {
		this.loadChart();
	}

	client() {
		return this.props.clients.find(c => c.clientId === parseInt(this.state.clientId, 10)) || null;
	}

	recipientLabel() {
		const c = this.client();
		if (this.state.email.recipientEmail && this.props.task && this.props.task.emailAutomation) return `${this.state.email.recipientEmail}${this.state.email.recipientName ? ` (${this.state.email.recipientName})` : ""}`;
		if (c && c.contact && c.contact.email) return `${c.contact.email} (${[c.contact.name, c.contact.relation].filter(Boolean).join(" · ")})`;
		return "No client contact email — add a contact to the client first";
	}

	loadChart = async () => {
		if (!this.state.clientId) return;
		const result = await BackofficeService.listChartOfAccounts({ clientId: this.state.clientId });
		this.setState({ chart: result.ok ? result.data : [] });
	};

	onClientChange = async e => {
		const clientId = e.target.value;
		await new Promise(r => this.setState({ clientId, accounts: this.state.accounts.filter(a => !a.chartOfAccountId) }, r));
		this.loadChart();
	};

	set = (key, value) => this.setState({ [key]: value });
	setEmail = (key, value) => this.setState({ email: Object.assign({}, this.state.email, { [key]: value }) });
	setHours = (bucket, role, value) => this.setState({ [bucket]: Object.assign({}, this.state[bucket], { [role]: value }) });

	toggleAccounts = () => {
		const on = !this.state.accountsOn;
		let accounts = this.state.accounts;
		if (on && !accounts.length) accounts = defaultAccountsFor(this.state.title, this.state.chart).map(a => ({ chartOfAccountId: a.id, glCode: a.glCode, name: a.name, type: a.type, isDone: false }));
		this.setState({ accountsOn: on, accounts });
	};

	toggleChartAccount = (a, checked) => {
		let accounts = this.state.accounts.filter(x => x.chartOfAccountId !== a.id);
		if (checked) accounts = accounts.concat([{ chartOfAccountId: a.id, glCode: a.glCode, name: a.name, type: a.type, isDone: false }]);
		this.setState({ accounts });
	};

	addCustomAccount = () => {
		const name = this.state.newAccount.trim();
		if (!name) return;
		this.setState({ accounts: this.state.accounts.concat([{ chartOfAccountId: null, glCode: null, name, type: this.state.newAccountType, isDone: false }]), newAccount: "" });
	};

	save = async () => {
		const s = this.state;
		const title = s.title.trim();
		if (!title) return this.setState({ error: "Give the task a name." });
		if (!s.clientId) return this.setState({ error: "Choose a client." });
		if (s.dueDate < s.startDate) return this.setState({ error: "The due date is before the start date." });
		this.setState({ saving: true, error: null });

		const client = this.client();
		const groupName = s.groupSelect === NEW_GROUP ? s.groupName.trim() : s.groupSelect;
		const payload = {
			clientId: parseInt(s.clientId, 10),
			service: s.service,
			title,
			description: s.description,
			startDate: s.startDate,
			dueDate: s.dueDate,
			frequency: s.frequency,
			repeatRule: s.repeatRule,
			status: s.status,
			groupName: groupName || null,
			preparerUserId: s.preparerUserId ? parseInt(s.preparerUserId, 10) : null,
			reviewerUserId: s.reviewerUserId ? parseInt(s.reviewerUserId, 10) : null,
			managerUserId: s.managerUserId ? parseInt(s.managerUserId, 10) : null,
			budget: { preparer: parseFloat(s.budget.preparer) || 0, reviewer: parseFloat(s.budget.reviewer) || 0, manager: parseFloat(s.budget.manager) || 0 },
			logged: { preparer: parseFloat(s.logged.preparer) || 0, reviewer: parseFloat(s.logged.reviewer) || 0, manager: parseFloat(s.logged.manager) || 0 },
			accounts: s.accountsOn ? s.accounts : [],
			email: s.emailOn
				? Object.assign({ enabled: true }, s.email, {
						subject: s.email.subject.trim() || `${title} request`,
						recipientEmail: s.email.recipientEmail || (client && client.contact ? client.contact.email : ""),
						recipientName: s.email.recipientName || (client && client.contact ? client.contact.name : ""),
				  })
				: { enabled: false },
		};
		if (this.props.periodStart && !this.props.task) payload.periodStart = this.props.periodStart;

		const result = this.props.task ? await BackofficeService.updateTask(Object.assign({ taskId: this.props.task.id }, payload)) : await BackofficeService.createTask(payload);
		this.setState({ saving: false });
		if (!result.ok) {
			this.setState({ error: result.error });
			return;
		}
		if (this.props.task) this.props.onSaved({ task: result.data.task, nextTask: result.data.nextTask, created: false, emailWarning: result.data.emailWarning });
		else this.props.onSaved({ task: result.data, created: true, emailWarning: result.data.emailWarning });
	};

	renderUserSelect(roleKey) {
		const role = ROLES[roleKey];
		const field = role.idField;
		return (
			<div className="bo-field" key={roleKey}>
				<label>
					<span className={clsx("bo-role", `bo-role--${role.cls}`)}>{role.short}</span>
					{role.label}
				</label>
				<select value={this.state[field] || ""} onChange={e => this.set(field, e.target.value)} className={`ft-bo-task-${roleKey}`}>
					<option value="">Unassigned</option>
					{this.props.users.map(u => (
						<option key={u.id} value={u.id}>
							{u.name}
							{u.title ? ` (${u.title})` : ""}
						</option>
					))}
				</select>
			</div>
		);
	}

	render() {
		const { task, clients, gmailStatus, onClose } = this.props;
		const s = this.state;
		const b = (parseFloat(s.budget.preparer) || 0) + (parseFloat(s.budget.reviewer) || 0) + (parseFloat(s.budget.manager) || 0);
		const l = (parseFloat(s.logged.preparer) || 0) + (parseFloat(s.logged.reviewer) || 0) + (parseFloat(s.logged.manager) || 0);
		const chartSelected = a => s.accounts.some(x => x.chartOfAccountId === a.id);
		const customs = s.accounts.filter(a => !a.chartOfAccountId);
		const client = this.client();

		return (
			<BoModal
				title={task ? "Edit task" : "New backoffice task"}
				icon={<I.cal />}
				onClose={onClose}
				footerLeft={
					<>
						<I.refresh />
						Recurring tasks create the next occurrence when this one is marked complete.
					</>
				}
				footer={
					<>
						<button className="bo-btn" type="button" onClick={onClose}>
							Cancel
						</button>
						<button className="bo-btn bo-btn--primary ft-bo-task-save" type="button" onClick={this.save} disabled={s.saving}>
							{s.saving ? "Saving…" : "Save task"}
						</button>
					</>
				}
			>
				{s.error && <div className="bo-field__error">{s.error}</div>}
				<div className="bo-field">
					<label htmlFor="boTitle">Task name</label>
					<input id="boTitle" className="ft-bo-task-title" type="text" value={s.title} onChange={e => this.set("title", e.target.value)} placeholder="e.g. Reconcile bank and credit card statements" autoFocus />
				</div>
				<div className="bo-grid2">
					<div className="bo-field">
						<label htmlFor="boClient">Client</label>
						<select id="boClient" value={s.clientId || ""} onChange={this.onClientChange} disabled={!!task}>
							{clients.map(c => (
								<option key={c.id} value={c.clientId}>
									{c.name}
								</option>
							))}
						</select>
					</div>
					<div className="bo-field">
						<label htmlFor="boService">Service</label>
						<select id="boService" value={s.service} onChange={e => this.set("service", e.target.value)}>
							{SERVICE_ORDER.map(k => (
								<option key={k} value={k}>
									{SERVICES[k].label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="bo-field">
					<label htmlFor="boDesc">
						Description <small>(optional)</small>
					</label>
					<textarea id="boDesc" value={s.description} onChange={e => this.set("description", e.target.value)} placeholder="What needs to be done and where to find it" />
				</div>
				<div className="bo-grid3">
					<div className="bo-field">
						<label htmlFor="boStart">Start date</label>
						<input id="boStart" type="date" value={s.startDate} onChange={e => this.set("startDate", e.target.value)} />
					</div>
					<div className="bo-field">
						<label htmlFor="boDue">Due date</label>
						<input id="boDue" type="date" value={s.dueDate} onChange={e => this.set("dueDate", e.target.value)} />
					</div>
					<div className="bo-field">
						<label htmlFor="boFreq">Repeats</label>
						<select id="boFreq" value={s.frequency} onChange={e => this.set("frequency", e.target.value)}>
							{Object.keys(FREQUENCIES).map(k => (
								<option key={k} value={k}>
									{FREQUENCIES[k]}
								</option>
							))}
						</select>
					</div>
				</div>
				{s.frequency !== "one_time" && (
					<div className="bo-field">
						<label htmlFor="boRule">Repeat rule</label>
						<select id="boRule" value={s.repeatRule} onChange={e => this.set("repeatRule", e.target.value)}>
							{Object.keys(REPEAT_RULES).map(k => (
								<option key={k} value={k}>
									{REPEAT_RULES[k]}
								</option>
							))}
						</select>
					</div>
				)}
				<div className="bo-roleset">{ROLE_ORDER.map(r => this.renderUserSelect(r))}</div>

				<div className="bo-callout bo-callout--blue">
					<h4>
						<I.clock />
						Time budget <small>per occurrence, in hours</small>
					</h4>
					<div className="bo-budgrid">
						<span />
						<span className="bo-budgrid__h">Budget</span>
						<span className="bo-budgrid__h">Logged</span>
						{ROLE_ORDER.map(r => (
							<React.Fragment key={r}>
								<span className="bo-budgrid__rl">
									<span className={clsx("bo-role", `bo-role--${ROLES[r].cls}`)}>{ROLES[r].short}</span>
									{ROLES[r].label}
								</span>
								<input type="number" min="0" step="0.25" value={s.budget[r]} onChange={e => this.setHours("budget", r, e.target.value)} aria-label={`${ROLES[r].label} budget hours`} />
								<input type="number" min="0" step="0.25" value={s.logged[r]} onChange={e => this.setHours("logged", r, e.target.value)} aria-label={`${ROLES[r].label} logged hours`} />
							</React.Fragment>
						))}
					</div>
					<div className="bo-callout__note">
						<b>{hrs(b)}h</b> budgeted · <b>{hrs(l)}h</b> logged{l > b ? <span className="bo-var-pos"> · over budget by {hrs(l - b)}h</span> : null}. Feeds the Capacity report; recurring tasks carry the budget to each new occurrence.
					</div>
				</div>

				<div className="bo-toggle-row">
					<div>
						<div className="bo-toggle-row__t">Track accounts on this task</div>
						<div className="bo-toggle-row__d">List the accounts this task covers and tick each one off as it's done.</div>
					</div>
					<button className={clsx("bo-tog", { "bo-tog--on": s.accountsOn })} type="button" aria-label="Toggle accounts" onClick={this.toggleAccounts} />
				</div>
				{s.accountsOn && (
					<div className="bo-callout bo-callout--green">
						<h4>
							<I.card />
							Accounts <small>{s.accounts.length ? `(${s.accounts.length} selected)` : ""}</small>
						</h4>
						<div className="bo-callout__sub">From this client's chart of accounts</div>
						{!s.chart.length && <div className="bo-callout__note">No bank, credit card, clearing or loan accounts found in this client's chart of accounts. Add accounts below instead.</div>}
						<div className="bo-acct-pick">
							{s.chart.map(a => (
								<label key={a.id} className={clsx({ "bo-acct-pick--on": chartSelected(a) })}>
									<input type="checkbox" checked={chartSelected(a)} onChange={e => this.toggleChartAccount(a, e.target.checked)} />
									{a.name}
									{a.glCode && <span className="bo-gl">{a.glCode}</span>}
								</label>
							))}
							{customs.map(a => (
								<label key={`c-${a.name}`} className="bo-acct-pick--on">
									<input type="checkbox" checked onChange={() => this.setState({ accounts: s.accounts.filter(x => x.chartOfAccountId || x.name !== a.name) })} />
									{a.name}
									<span className="bo-gl">{ACCOUNT_TYPES[a.type]}</span>
								</label>
							))}
						</div>
						<div className="bo-callout__sub">Or add one that isn't listed</div>
						<div className="bo-aadd" style={{ padding: 0 }}>
							<input type="text" value={s.newAccount} onChange={e => this.set("newAccount", e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), this.addCustomAccount())} placeholder="e.g. Scotiabank Line of Credit ••7710" />
							<select value={s.newAccountType} onChange={e => this.set("newAccountType", e.target.value)} aria-label="Account type">
								{Object.keys(ACCOUNT_TYPES).map(k => (
									<option key={k} value={k}>
										{ACCOUNT_TYPES[k]}
									</option>
								))}
							</select>
							<button className="bo-btn bo-btn--sm" type="button" onClick={this.addCustomAccount}>
								Add account
							</button>
						</div>
					</div>
				)}

				<div className="bo-grid2">
					<div className="bo-field">
						<label htmlFor="boStatus">Status</label>
						<select id="boStatus" value={s.status} onChange={e => this.set("status", e.target.value)}>
							{STATUS_ORDER.map(k => (
								<option key={k} value={k}>
									{STATUSES[k].label}
								</option>
							))}
						</select>
					</div>
					<div className="bo-field">
						<label htmlFor="boGroup">
							Group under <small>(optional)</small>
						</label>
						<select id="boGroup" value={s.groupSelect} onChange={e => this.setState({ groupSelect: e.target.value, groupName: e.target.value === NEW_GROUP ? "" : e.target.value })}>
							<option value="">None</option>
							{GROUP_PRESETS.map(g => (
								<option key={g} value={g}>
									{g}
								</option>
							))}
							<option value={NEW_GROUP}>+ New group…</option>
						</select>
						{s.groupSelect === NEW_GROUP && <input type="text" value={s.groupName} onChange={e => this.set("groupName", e.target.value)} placeholder="Group name" />}
					</div>
				</div>

				<div className="bo-toggle-row">
					<div>
						<div className="bo-toggle-row__t">Client action required</div>
						<div className="bo-toggle-row__d">Send an automated email to the client contact, with follow-up reminders, and show each send on the calendar.</div>
					</div>
					<button className={clsx("bo-tog", "ft-bo-task-email-toggle", { "bo-tog--on": s.emailOn })} type="button" aria-label="Toggle client email" onClick={() => this.set("emailOn", !s.emailOn)} />
				</div>
				{s.emailOn && (
					<div className="bo-callout">
						<h4>
							<I.mail />
							Automated client email
						</h4>
						<div className="bo-grid2">
							<div className="bo-field">
								<label>Send from</label>
								<input type="text" readOnly value={gmailStatus && gmailStatus.connected ? `${gmailStatus.gmailAddress} (Google Workspace)` : "Workflow mail (no Gmail connected — replies go to your email)"} />
							</div>
							<div className="bo-field">
								<label>Recipient</label>
								<input type="text" readOnly value={this.recipientLabel()} />
							</div>
						</div>
						{!(client && client.contact && client.contact.email) && !s.email.recipientEmail && <div className="bo-field__error">This client has no contact email. The task will save, but the automation will not activate until a contact is added.</div>}
						<div className="bo-field">
							<label>Subject</label>
							<input type="text" value={s.email.subject} onChange={e => this.setEmail("subject", e.target.value)} placeholder={`${s.title || "Task"} request`} />
						</div>
						<div className="bo-field">
							<label>
								Message template <small>{"{{contact_first_name}} {{client_name}} {{period}} {{task_name}} {{due_date}}"}</small>
							</label>
							<textarea value={s.email.body} onChange={e => this.setEmail("body", e.target.value)} />
						</div>
						<div className="bo-grid3">
							<div className="bo-field">
								<label>First send</label>
								<select value={s.email.firstSend} onChange={e => this.setEmail("firstSend", e.target.value)}>
									{Object.keys(FIRST_SENDS).map(k => (
										<option key={k} value={k}>
											{FIRST_SENDS[k]}
										</option>
									))}
								</select>
							</div>
							<div className="bo-field">
								<label>Send time</label>
								<input type="time" value={s.email.sendTime} onChange={e => this.setEmail("sendTime", e.target.value)} />
							</div>
							<div className="bo-field">
								<label>Follow-up cadence</label>
								<select value={s.email.cadence} onChange={e => this.setEmail("cadence", e.target.value)}>
									{Object.keys(CADENCES).map(k => (
										<option key={k} value={k}>
											{CADENCES[k]}
										</option>
									))}
								</select>
							</div>
						</div>
						<label className="bo-chk">
							<input type="checkbox" checked={s.email.markOnCalendar} onChange={e => this.setEmail("markOnCalendar", e.target.checked)} /> Mark scheduled sends on the calendar
						</label>
					</div>
				)}
			</BoModal>
		);
	}
}

export default TaskModal;
